import { useEffect, useState } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import style from '../components/transaction/TransactionTable/style.module.scss';
import TransactionLink from '../components/ui/TransactionLink';
import { formatCurrency } from '../utility/number.utils';
import { dateFormate, toUpperCase } from '../lib/constant';
import { convertToCSV } from '../lib/lib';
import apiInstance from '../config/axios';
import CustomDateRangePicker from '../components/common/DatePicker/CustomDateRangePicker';
import { SearchIcon } from 'lucide-react';
import TableComponent from '../components/Table';
import useAPI from '../hooks/useAPI';

interface IPaginationObject {
  limit: number;
  page: number;
  search: string;
  status: string;
  type: string;
  from: string;
  to: string;
}

interface ITransactionResponse {
  result: {
    tokenTransactionHistory: any[];
    totalTokenTransactionHistory: number;
    totalPages: number;
    transactions: any[];
    total: number;
  };
}

const statusOptions = [
  { label: 'Pending', value: 'PENDING' },
  { label: 'Success', value: 'SUCCESS' },
  { label: 'Failed', value: 'FAILED' },
];

const transactionTypeOptions = [
  { label: 'Credit', value: 'CREDITED' },
  { label: 'Debit', value: 'DEBITED' },
];

const MAX_DATE_RANGE_DAYS = 90;

// Helper function to format status text from SNAKE_CASE to Title Case
const formatStatusText = (status: string): string => {
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to determine status color
const getStatusColor = (status: string): string => {
  if (status === 'SUCCESS') {
    return 'success';
  } else if (status === 'FAILED') {
    return 'danger';
  } else if (status === 'PENDING') {
    return 'warning';
  } else {
    // All other statuses (ADD_CASH_INITIATED, etc.) show in warning/yellow
    return 'warning';
  }
};

function TransactionsAllPage() {
  // Pagination and filter states
  const [paginationData, setPaginationData] = useState<IPaginationObject>({
    page: 1,
    limit: 10,
    search: '',
    status: '',
    type: '',
    from: '',
    to: '',
  });
  const [isDownloading, setIsDownloading] = useState(false);

  // Date range states
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [dateError, setDateError] = useState<string>('');

  // Search debounce
  const [searchInput, setSearchInput] = useState('');

  // Build API URL with query parameters
  const buildApiUrl = () => {
    const params = new URLSearchParams();
    params.append('page', String(paginationData.page));
    params.append('limit', String(paginationData.limit));
    paginationData.status && params.append('status', paginationData.status);
    paginationData.type && params.append('type', paginationData.type);
    params.append('from', paginationData.from);
    params.append('to', paginationData.to);
    if (paginationData.search) {
      params.append('search', paginationData.search);
    }
    return `/token/transaction/history/all?${params.toString()}`;
  };

  // Fetch transaction history using useAPI
  const { data: transactionResponse, isLoading: isTransactionLoading } = useAPI<
    void,
    ITransactionResponse
  >({
    url: buildApiUrl(),
    method: 'GET',
    dependencies: [
      paginationData.page,
      paginationData.limit,
      paginationData.status,
      paginationData.type,
      paginationData.from,
      paginationData.to,
      paginationData.search,
    ],
    onSuccess: () => {},
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch transactions',
      );
    },
  });

  const transactionData = transactionResponse?.result?.transactions || [];
  const totalCount = transactionResponse?.result?.total || 0;
  console.log('totalCount', totalCount);

  // Validate date range (max 90 days)
  const validateDateRange = (
    start: Date | undefined,
    end: Date | undefined,
  ): boolean => {
    if (!start || !end) return true;

    const diffInDays = moment(end).diff(moment(start), 'days');
    if (diffInDays > MAX_DATE_RANGE_DAYS) {
      setDateError(`Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days`);
      return false;
    }
    setDateError('');
    return true;
  };

  // Handle date range change with validation
  const handleEndDateChange = (date: Date | undefined) => {
    setEndDate(date);
    if (startDate && date) {
      if (validateDateRange(startDate, date)) {
        setPaginationData(prev => ({
          ...prev,
          page: 1,
          from: moment(startDate).format('YYYY-MM-DD'),
          to: moment(date).format('YYYY-MM-DD'),
        }));
      }
    } else if (!date) {
      setDateError('');
      setPaginationData(prev => ({
        ...prev,
        page: 1,
        from: '',
        to: '',
      }));
    }
  };

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== paginationData.search) {
        setPaginationData(prev => ({
          ...prev,
          page: 1,
          search: searchInput,
        }));
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Handle status filter change
  const handleStatusChange = (value: string) => {
    setPaginationData(prev => ({
      ...prev,
      page: 1,
      status: value === 'All' ? '' : value,
    }));
  };

  // Handle type filter change
  const handleTypeChange = (value: string) => {
    setPaginationData(prev => ({
      ...prev,
      page: 1,
      type: value === 'All' ? '' : value,
    }));
  };

  // Handle pagination change from TableComponent
  const handleFetchMoreData = (pageSize: number, page: number) => {
    setPaginationData(prev => ({
      ...prev,
      limit: pageSize,
      page: page,
    }));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchInput('');
    setDateRange([null, null]);
    setStartDate(undefined);
    setEndDate(undefined);
    setDateError('');
    setPaginationData({
      page: 1,
      limit: 10,
      search: '',
      status: '',
      type: '',
      from: '',
      to: '',
    });
  };

  const columns = [
    {
      label: 'Transaction ID',
      id: 'transactionId',
      width: 80,
      renderCell: (row: any) => {
        return row?.transactionId ? (
          <TransactionLink transactionNo={row?.transactionId} />
        ) : (
          '-'
        );
      },
    },
    {
      label: 'Amount',
      id: 'tokenAmount',
      renderCell: (row: any) => (
        <span>
          {formatCurrency(parseInt(row?.amount), { currency: '', decimals: 0 })}
        </span>
      ),
    },
    //     {
    //       label: 'Created By',
    //       id: 'createdByEmail',
    //       renderCell: (row: any) => row?.createdByEmail,
    //     },

    {
      label: 'Credit/Debit',
      id: 'type',
      renderCell: (row: any) => <div>{toUpperCase(row?.type)}</div>,
    },
    {
      label: 'Status',
      id: 'status',
      renderCell: (row: any) => (
        <div
          className={`fw-semibold text-${getStatusColor(row?.status)} text-capitalize`}
        >
          {formatStatusText(row?.status)}
        </div>
      ),
    },
    {
      label: 'Receiver Name',
      id: 'receiverByEmail',
      renderCell: (row: any) =>
        row?.receiverfirstname
          ? row?.receiverfirstname + ' ' + row?.receiverlastname
          : '-',
    },
    {
      label: 'Received By',
      id: 'receiverByEmail',
      renderCell: (row: any) => row?.receiveremail,
    },
    {
      label: 'Txn Date & Time',
      id: 'createdAt',
      renderCell: (row: any) =>
        row?.dateTime ? dateFormate(row.dateTime) : '-',
    },
    {
      label: 'Remarks',
      id: 'remarks',
      renderCell: (row: any) => <span>{toUpperCase(row?.remarks) || '-'}</span>,
    },
  ];

  const hasActiveFilters =
    paginationData.search ||
    paginationData.status ||
    paginationData.type ||
    paginationData.from ||
    paginationData.to;

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const params = new URLSearchParams();
      params.append('status', paginationData.status || '');
      params.append('type', paginationData.type || '');
      params.append('from', paginationData.from);
      params.append('to', paginationData.to);
      params.append('search', paginationData.search || '');

      const response = await apiInstance.get(
        `/token/transaction/history/download?${params.toString()}`,
      );

      const transactionHistory = response.data.result.requestedData;

      if (!transactionHistory || transactionHistory.length === 0) {
        toast.error('No data available to download');
        return;
      }

      const csvContent = convertToCSV(transactionHistory);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = moment().format('DDMMYYYYHHmmss');
      link.setAttribute('download', `Transaction${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download error:', error);
      toast.error(error?.response?.data?.message || 'Failed to download CSV');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={style.transactionWrapper}>
      <div className={style.transactionHeader}>
        <h5 className="mb-0">Transaction Details</h5>
      </div>

      <div className="px-3">
        {/* Filters Section */}
        <div className="d-flex flex-wrap gap-3 mb-3 align-items-start">
          {/* Search Input */}
          <div
            className="flex-grow-1"
            style={{ minWidth: '180px', maxWidth: '250px' }}
          >
            <Form.Label className="small fw-semibold mb-1">Search</Form.Label>
            <InputGroup size="sm">
              <InputGroup.Text className="bg-white">
                <SearchIcon size={14} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by email"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="border-start-0 custom-input"
                size="sm"
              />
            </InputGroup>
          </div>

          {/* Date Range Picker */}
          <div style={{ minWidth: '220px' }} className="d-flex flex-column">
            <Form.Label className="small fw-semibold mb-1">
              Date Range{' '}
              <span
                className="text-muted fw-normal"
                style={{ fontSize: '11px' }}
              ></span>
            </Form.Label>
            <CustomDateRangePicker
              dateRange={dateRange}
              setDateRange={setDateRange}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              // @ts-ignore
              setEndDate={handleEndDateChange}
              placeholder="Select date range"
            />
            {dateError && (
              <Form.Text className="text-danger small">{dateError}</Form.Text>
            )}
          </div>

          {/* Transaction Type Filter */}
          <div style={{ minWidth: '130px' }}>
            <Form.Label className="small fw-semibold mb-1">Type</Form.Label>
            <Form.Select
              className="rounded-2 custom-input"
              size="sm"
              value={paginationData.type || 'All'}
              onChange={e => handleTypeChange(e.target.value)}
            >
              <option value="All">All Types</option>
              {transactionTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </div>

          {/* Status Filter */}
          <div style={{ minWidth: '130px' }}>
            <Form.Label className="small fw-semibold mb-1">Status</Form.Label>
            <Form.Select
              className="rounded-2 custom-input"
              size="sm"
              value={paginationData.status || 'All'}
              onChange={e => handleStatusChange(e.target.value)}
            >
              <option value="All">All Status</option>
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <div className="d-flex flex-column">
              <Form.Label className="small fw-semibold mb-1 custom-filter">
                {/* <span className="text-muted fw-normal" style={{ fontSize: '11px' }}>
              
            </span> */}
              </Form.Label>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleClearFilters}
                className="rounded-2 custom-filter-button"
              >
                Clear Filters
              </Button>
            </div>
          )}

          {/* Download Button */}
          <div className="d-flex flex-column">
            <Form.Label className="small fw-semibold mb-1">&nbsp;</Form.Label>
            <Button
              className={style.actionButton}
              onClick={handleDownload}
              disabled={isDownloading}
            >
              {isDownloading ? 'Downloading...' : 'Download CSV'}
            </Button>
          </div>
        </div>

        {/* Transaction Table */}

        <TableComponent
          data={transactionData}
          columns={columns}
          totalCount={totalCount}
          fetchMore={handleFetchMoreData}
          isServerSidePagination
          perPageSize={paginationData.limit}
          activePage={paginationData.page}
          loading={isTransactionLoading}
          customBodyCss="custom-table-emp-transfer-history"
        />
      </div>
    </div>
  );
}

export default TransactionsAllPage;
